// https://developer.voyado.com/docs/tracking/web-activity-tracking

import { env } from "@/env";
import type { Variant } from "types";
import { parseJwt } from "./parseJwt";

type VoyadoMarket = {
  contentfulLocale: string;
};

export type VoyadoTrackableCart = {
  id: string | number;
  items: { partNo: string; quantity: number }[];
};

function canCallVoyado() {
  return typeof window !== "undefined" && typeof window.va === "function";
}

function getProductCartItems(cart: VoyadoTrackableCart) {
  const freightPartNo = env.NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO;
  return cart.items.filter((item) => item.partNo !== freightPartNo);
}

export function getVoyadoContactIdFromToken(token: string) {
  try {
    const parsedToken = parseJwt(token);
    const voyadoContactId = parsedToken["ng.voyadoid"];
    return typeof voyadoContactId === "string" && voyadoContactId
      ? voyadoContactId
      : undefined;
  } catch {
    return undefined;
  }
}

export function sendVoyadoEmptyCartEvent(id?: string | number) {
  if (!canCallVoyado() || id === undefined || id === "") {
    return;
  }

  window.va?.("emptyCart", {
    cartRef: String(id),
  });
}

export function sendVoyadoCartChangeEvent({
  market,
  cart,
}: {
  market: VoyadoMarket;
  cart?: VoyadoTrackableCart | null;
}) {
  if (!canCallVoyado() || !cart) {
    return;
  }

  const items = getProductCartItems(cart).map((cartItem) => ({
    itemId: cartItem.partNo,
    quantity: cartItem.quantity,
  }));

  if (!items.length) {
    sendVoyadoEmptyCartEvent(cart.id);
    return;
  }

  window.va?.("cart", {
    cartRef: String(cart.id),
    locale: market.contentfulLocale,
    items,
  });
}

export function setVoyadoContactId(token: string) {
  if (!canCallVoyado()) return;

  const voyadoContactId = getVoyadoContactIdFromToken(token);
  if (!voyadoContactId) return;

  try {
    window.va?.("setContactId", voyadoContactId);
  } catch (error) {
    console.warn("Failed to set Voyado Contact ID: ", error);
  }
}

export function sendVoyadoProductViewEvent({
  market,
  item,
}: {
  market: VoyadoMarket;
  item: Variant;
}) {
  try {
    if (!canCallVoyado()) {
      return;
    }

    const categoryName = item.category.join(" / ");

    window.va?.("productview", {
      categoryName,
      itemId: item.id,
      locale: market.contentfulLocale,
    });
  } catch (error) {
    console.warn("Failed to send Voyado product view event: ", error);
  }
}

export function sendVoyadoEmailRegistrationEvent(email: string) {
  if (!canCallVoyado()) {
    return;
  }

  try {
    window.va?.("emailRegistration", {
      email,
    });
  } catch (error) {
    console.warn("Failed to send Voyado email registration event: ", error);
  }
}
